import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";

// Stops a person leaving a screen with unsaved work by accident. The app's
// router is a plain BrowserRouter (not a data router), so react-router's
// useBlocker isn't available: instead the guard catches clicks on in-app
// links before the router sees them, the browser's Back and Forward, and
// closing or reloading the tab. Code that calls navigate() itself is not
// caught; screens that do that should check `when` first.

// Where a click on a link would take the person, as an in-app path, when
// the guard should ask first; null when the click should go ahead as usual
// (another site, a new tab, a download, the same page, or nothing to lose).
export function leaveTarget({ when, href, target, download, button = 0, metaKey, ctrlKey, shiftKey, altKey, defaultPrevented }, here) {
  if (!when || !href || defaultPrevented) return null;
  if (button !== 0 || metaKey || ctrlKey || shiftKey || altKey) return null;
  if (download != null && download !== false) return null;
  if (target && target !== "_self") return null;
  let url;
  try {
    url = new URL(href, here.href);
  } catch (e) {
    return null;
  }
  if (url.origin !== here.origin) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  // A jump within the page (#section) loses nothing.
  if (url.pathname === here.pathname && url.search === here.search) return null;
  return path;
}

// "Your changes to the Refund policy will be lost.", "… to the Terms and
// conditions and the Refund policy …", or plain "Your changes will be
// lost." when the screen can't say. Each name comes with its own "the".
export function leaveMessage(sections = []) {
  const names = [...new Set(sections.filter(Boolean))];
  if (!names.length) return "Your changes will be lost.";
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Your changes to ${list} will be lost.`;
}

// `when`: there is something to lose. Returns the dialog's props; render
// <LeaveDialog {...guard} message={…} /> on the screen.
export default function useLeaveGuard(when) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const whenRef = useRef(when);
  whenRef.current = when;
  // Set while the guard itself is leaving, so its own navigation goes through.
  const leaving = useRef(false);

  useEffect(() => {
    if (!when) return undefined;
    const here = () => window.location;
    // Where the person is now, so Back can be undone if they choose to stay.
    let stayState = window.history.state;
    let stayUrl = window.location.href;

    const onClick = (event) => {
      if (leaving.current) return;
      const link = event.target?.closest?.("a[href]");
      if (!link) return;
      const path = leaveTarget(
        {
          when: whenRef.current,
          href: link.getAttribute("href"),
          target: link.getAttribute("target"),
          download: link.hasAttribute("download") ? "" : null,
          button: event.button,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          defaultPrevented: event.defaultPrevented,
        },
        here()
      );
      if (!path) return;
      // Capture phase on the document: the router's own handler never runs.
      event.preventDefault();
      event.stopPropagation();
      setPending(path);
    };

    // Back or Forward: the address has already changed. Put it back, keep
    // the router from moving, and ask.
    const onPop = (event) => {
      if (leaving.current || !whenRef.current) return;
      event.stopImmediatePropagation();
      const loc = here();
      const path = `${loc.pathname}${loc.search}${loc.hash}`;
      window.history.pushState(stayState, "", stayUrl);
      setPending(path);
    };

    // Closing or reloading the tab: the browser asks in its own words.
    const onUnload = (event) => {
      if (!whenRef.current || leaving.current) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPop, true);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPop, true);
      window.removeEventListener("beforeunload", onUnload);
      stayState = null;
      stayUrl = null;
    };
  }, [when]);

  const stay = useCallback(() => setPending(null), []);
  const leave = useCallback(() => {
    const path = pending;
    setPending(null);
    if (!path) return;
    leaving.current = true;
    navigate(path);
    // The screen usually unmounts; if it doesn't (same component, new
    // route), the guard works again from here.
    setTimeout(() => {
      leaving.current = false;
    }, 0);
  }, [navigate, pending]);

  return { open: !!pending, onStay: stay, onLeave: leave };
}

// The in-app question: "Leave without saving?" with Stay and Leave.
export function LeaveDialog({ open, onStay, onLeave, message }) {
  const phone = useMediaQuery((t) => t.breakpoints.down("sm"));
  return (
    <Dialog open={open} onClose={onStay} fullWidth maxWidth="xs" aria-labelledby="leave-title" aria-describedby="leave-text">
      <DialogTitle id="leave-title" sx={{ fontSize: "1.125rem", fontWeight: 700, pb: 1 }}>
        Leave without saving?
      </DialogTitle>
      <DialogContent>
        <Typography id="leave-text" color="text.secondary">
          {message || leaveMessage()}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2.5, gap: 1, flexDirection: phone ? "column-reverse" : "row", "& > :not(:first-of-type)": { ml: phone ? 0 : 1 } }}
      >
        <ActionButton variant="contained" size="medium" onClick={onStay} autoFocus sx={{ width: phone ? "100%" : "auto" }}>
          Stay
        </ActionButton>
        <ActionButton
          variant="outlined"
          tone="destructive"
          size="medium"
          onClick={onLeave}
          sx={{ width: phone ? "100%" : "auto", border: 1, borderColor: (t) => t.palette.secondary.main }}
        >
          Leave
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

LeaveDialog.propTypes = {
  open: PropTypes.bool,
  onStay: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  message: PropTypes.string,
};
